import React, { useState } from 'react'
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import getEnvConfig from '../../../../config'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

function SetPassword() {
  const { backendUrl } = getEnvConfig()
  const query = useQuery()
  const navigate = useNavigate()
  const token = query.get('token')
  const email = query.get('email')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [verificationSid, setVerificationSid] = useState('')
  const [isOtpRequested, setIsOtpRequested] = useState(false)
  const [isOtpVerified, setIsOtpVerified] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleRequestOtp = async () => {
    setError('')
    setSuccess('')
    if (!email) {
      setError('Missing email in URL.')
      return
    }
    try {
      const response = await fetch(backendUrl + '/users/password-reset/send-otp/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })
      const data = await response.json()
      if (response.ok) {
        setVerificationSid(data.verification_sid)
        setIsOtpRequested(true)
        setSuccess('OTP sent to your email.')
      } else {
        setError(data.detail || data.message || 'Failed to send OTP.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    }
  }

  const handleVerifyOtp = async () => {
    setError('')
    setSuccess('')
    if (!email || !otp || !verificationSid) {
      setError('Missing email, OTP, or verification SID.')
      return
    }
    setIsVerifyingOtp(true)
    try {
      const response = await fetch(backendUrl + '/users/password-reset/verify-otp/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, verification_sid: verificationSid })
      })
      const data = await response.json()
      if (data.success) {
        setIsOtpVerified(true)
        setSuccess('OTP verified. You can now set your new password.')
      } else {
        setError(data.error || 'Invalid OTP.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    if (!token || !email || !verificationSid) {
      setError('Missing token, email, or verification SID.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    setIsSubmitting(true)
    try {
      const response = await fetch(backendUrl + '/users/reset-password/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword, token, email, verification_sid: verificationSid })
      })
      const data = await response.json()
      if (response.ok) {
        setSuccess('Your password has been reset successfully. You may now log in.')
        setNewPassword('')
        setConfirmPassword('')
        setTimeout(() => navigate('/Users/Auth/Reset'), 3000)
      } else {
        setError(data.detail || data.message || 'Failed to reset password.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container fluid className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <Card.Body>
              <Card.Title as="h3">Set New Password</Card.Title>
              <p>Enter your new password below. You must verify OTP before setting a new password.</p>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              <div className="mb-3">
                <Button variant="primary" onClick={handleRequestOtp} disabled={isOtpRequested}>
                  {isOtpRequested ? 'OTP Sent' : 'Request OTP'}
                </Button>
              </div>
              {isOtpRequested && !isOtpVerified && (
                <Form.Group controlId="otp" className="mb-3">
                  <Form.Label>Enter OTP</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={e => setOtp(e.target.value)}
                    maxLength={6}
                  />
                  <Button className="mt-2" variant="secondary" onClick={handleVerifyOtp} disabled={isVerifyingOtp}>
                    {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                </Form.Group>
              )}
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="newPassword">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    disabled={!isOtpVerified}
                  />
                </Form.Group>
                <Form.Group controlId="confirmPassword" className="mt-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={!isOtpVerified}
                  />
                </Form.Group>
                <hr />
                <Button type="submit" variant="dark" disabled={isSubmitting || !isOtpVerified}>
                  {isSubmitting ? 'Resetting...' : 'Set Password'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default SetPassword 